"use client";

import {
  composeRefs,
  useComposedRefs,
  useControllableState,
} from "radix-ui/internal";
import { Form } from "radix-ui";
import { Grid } from "@radix-ui/themes";
import * as React from "react";
import { TextField } from "./elements.js";

interface OptContextType {
  value: string[];
  readOnly?: boolean;
  state?: "valid" | "invalid";
  onEnterPressed: () => void;
  onChildAdd: (input: HTMLInputElement) => void;
  onCharChange: (char: string, index: number) => void;
  allChildrenAdded: boolean;
}

const OtpContext = React.createContext<OptContextType | undefined>(undefined);

type OtpRootProps = React.ComponentPropsWithoutRef<typeof Grid> & {
  onValueChange?: (value: string) => void;
  id?: string;
  name?: string;
  readOnly?: boolean;
  state?: "valid" | "invalid";
  value?: string;
  defaultValue?: string;
  autoSubmit?: boolean;
};

export const Root = React.forwardRef<HTMLInputElement, OtpRootProps>(
  function Root(
    {
      name,
      id,
      defaultValue,
      value: valueProp,
      onValueChange,
      autoSubmit,
      children,
      readOnly,
      state,
      ...gridProps
    },
    forwardedRef,
  ) {
    const [lastCharIndex, setLastCharIndex] = React.useState<number>(0);
    const [allChildrenAdded, setAllChildrenAdded] =
      React.useState<boolean>(false);
    const childCount = React.Children.count(children);

    const [value, setValue] = useControllableState({
      prop: getValueAsArray(valueProp, childCount),
      defaultProp: getValueAsArray(defaultValue, childCount) ?? [],
      onChange: (value) => onValueChange?.(value.join("")),
    });

    const hiddenInputRef = React.useRef<HTMLInputElement>(null);
    const childrenRefs = React.useRef<HTMLInputElement[]>([]);

    const attemptAutoSubmit = React.useCallback(
      (enterPressed = false) => {
        if (
          autoSubmit &&
          value &&
          value.every((char) => char !== "") &&
          (enterPressed || lastCharIndex + 1 === childCount)
        ) {
          hiddenInputRef.current?.form?.requestSubmit();
        }
      },
      [value, childCount, lastCharIndex, autoSubmit],
    );

    const handleEnterPressed = React.useCallback(
      () => attemptAutoSubmit(true),
      [attemptAutoSubmit],
    );

    const handleChildAdd = React.useCallback(
      (input: HTMLInputElement) => {
        if (input) {
          input.dataset.index = `${childrenRefs.current.length}`;
          childrenRefs.current.push(input);
        } else {
          childrenRefs.current.pop();
        }

        if (childrenRefs.current.length === childCount) {
          setAllChildrenAdded(true);
        }
      },
      [childCount],
    );

    const handleCharChange = React.useCallback(
      (char: string, index: number) => {
        setValue((previousValue) => {
          const arrayToCopy = previousValue ?? createEmptyArray(childCount);
          const newValue = [...arrayToCopy];
          newValue[index] = char;
          return newValue;
        });
        setLastCharIndex(index);
      },
      [childCount, setValue],
    );

    const otpContext = React.useMemo(
      () => ({
        value: value ?? createEmptyArray(childCount),
        readOnly,
        state,
        allChildrenAdded,
        onEnterPressed: handleEnterPressed,
        onChildAdd: handleChildAdd,
        onCharChange: handleCharChange,
      }),
      [
        value,
        allChildrenAdded,
        readOnly,
        state,
        childCount,
        handleEnterPressed,
        handleChildAdd,
        handleCharChange,
      ],
    );

    React.useEffect(attemptAutoSubmit, [attemptAutoSubmit]);

    return (
      <OtpContext.Provider value={otpContext}>
        <Grid
          columns={`repeat(${childCount}, 1fr)`}
          {...gridProps}
          onPaste={(event: React.ClipboardEvent<HTMLDivElement>) => {
            event.preventDefault();
            const pastedValue = event.clipboardData.getData("Text");
            const sanitizedValue = pastedValue
              .replace(/[^\d]/g, "")
              .slice(0, childCount);
            const value = sanitizedValue
              .padEnd(childCount, "#")
              .split("")
              .map((char) => (char === "#" ? "" : char));

            setValue(value);
            setLastCharIndex(sanitizedValue.length - 1);

            const index = Math.min(sanitizedValue.length, childCount - 1);
            childrenRefs.current?.[index]?.focus();
          }}
        >
          {children}
          <input
            ref={composeRefs(forwardedRef, hiddenInputRef)}
            defaultValue={value?.join("")}
            minLength={childCount}
            name={name}
            type="hidden"
          />
        </Grid>
      </OtpContext.Provider>
    );
  },
);

interface InputProps extends React.ComponentProps<typeof TextField> {
  autoComplete?: "one-time-code" | "off";
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function Input(
    { style, readOnly, autoComplete = "off", ...props },
    forwardedRef,
  ) {
    const otpContext = useOptContext();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const composedInputRef = useComposedRefs(
      forwardedRef,
      inputRef,
      otpContext.onChildAdd,
    );

    // FIXME: This should be refactored
    // eslint-disable-next-line react-hooks/refs
    const index = Number(inputRef.current?.dataset.index ?? -1);
    const char = otpContext.value[index] ?? "";

    return (
      <Form.Field name={`otp-${index}`} asChild>
        <Form.Control asChild>
          <TextField
            ref={composedInputRef}
            autoComplete={index === 0 ? autoComplete : "off"}
            color={otpContext.state === "invalid" ? "red" : undefined}
            inputMode="numeric"
            maxLength={1}
            pattern="\d{1}"
            readOnly={readOnly ?? otpContext.readOnly}
            size="3"
            value={char}
            variant={otpContext.state === "invalid" ? "soft" : undefined}
            style={{
              ...style,
              height: "auto",
              "--text-field-padding": 0,
              textAlign: "center",
            }}
            onChange={(event) => {
              // Only update the value if it matches the input pattern (number only)
              if (event.target.validity.valid) {
                const char = event.target.value;
                const index = Number(event.target.dataset.index ?? -1);
                otpContext.onCharChange(char, index);
                if (char !== "") {
                  focusSibling(event.currentTarget, { back: char === "" });
                }
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft") {
                focusSibling(event.currentTarget, { back: true });
                event.preventDefault();
              } else if (event.key === "ArrowRight") {
                focusSibling(event.currentTarget);
                event.preventDefault();
              } else if (event.key === "Backspace" && char === "") {
                focusSibling(event.currentTarget, { back: true });
              } else if (event.key === "Enter" && char !== "") {
                otpContext.onEnterPressed();
              }
            }}
            {...props}
          />
        </Form.Control>
      </Form.Field>
    );
  },
);

const useOptContext = () => {
  const optContext = React.useContext(OtpContext);

  if (!optContext) {
    throw new Error(
      "OtpInput compound components cannot be rendered outside the OtpRoot component",
    );
  }

  return optContext;
};

function focusSibling(input: HTMLInputElement, { back = false } = {}) {
  const sibling = back
    ? input.parentElement?.previousSibling
    : input.parentElement?.nextSibling;
  const siblingInput = sibling?.firstChild;
  if (siblingInput && siblingInput instanceof HTMLInputElement) {
    siblingInput?.focus();
    siblingInput?.select();
  }
}

const getValueAsArray = (value: string | undefined, length: number) => {
  if (!value) {
    return undefined;
  }

  return createEmptyArray(length).map((_, index) => value?.[index] ?? "");
};

const createEmptyArray = (length: number): string[] =>
  Array.from<string>({ length }).fill("");
